class ProductAtelier extends HTMLElement {
  connectedCallback() {
    this._onProductRerender = () =>
      requestAnimationFrame(() => {
        this._observePurchaseJourney();
        this._enhanceProductDescriptions();
      });
    this.addEventListener("product:rerender", this._onProductRerender);
    this._observePurchaseJourney();
    this._enhanceProductDescriptions();
  }

  disconnectedCallback() {
    this.removeEventListener("product:rerender", this._onProductRerender);
    this._observer?.disconnect();
  }

  _observePurchaseJourney() {
    this._observer?.disconnect();
    this._mobileBar = this.querySelector("[data-product-atelier-mobile-bar]");
    this._purchaseControls = this.querySelector(".product-info__buy-buttons");
    this._exitBoundary =
      document.querySelector(".shopify-section--product-story-engine") ||
      document.querySelector(".shopify-section--product-recommendations") ||
      document.querySelector(".footer");

    if (!this._mobileBar || !this._purchaseControls || !this._exitBoundary) {
      this._mobileBar?.classList.remove("is-visible");
      return;
    }

    this._purchaseControlsPassed = false;
    this._exitBoundaryReached = false;
    this._observer = new IntersectionObserver(
      (entries) => this._handleIntersections(entries),
      { threshold: 0, rootMargin: "0px" }
    );
    this._observer.observe(this._purchaseControls);
    this._observer.observe(this._exitBoundary);
  }

  _handleIntersections(entries) {
    for (const entry of entries) {
      if (entry.target === this._purchaseControls) {
        this._purchaseControlsPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      }

      if (entry.target === this._exitBoundary) {
        this._exitBoundaryReached =
          entry.isIntersecting || entry.boundingClientRect.top <= window.innerHeight;
      }
    }

    this._mobileBar.classList.toggle(
      "is-visible",
      this._purchaseControlsPassed && !this._exitBoundaryReached
    );
  }

  _enhanceProductDescriptions() {
    for (const root of this.querySelectorAll("[data-description-accordions]")) {
      if (root.dataset.accordionsEnhanced === "true") continue;

      const source = root.querySelector("[data-description-accordion-source]");
      if (!source) continue;

      const headings = [...source.querySelectorAll("h2, h3, h4, h5, h6")].filter(
        (heading) =>
          heading.textContent.trim() &&
          !heading.closest("table, details, [data-skip-description-accordion]")
      );
      const minimumHeadings = Number.parseInt(root.dataset.minHeadings || "2", 10);

      if (headings.length < minimumHeadings) {
        root.dataset.accordionsEnhanced = "fallback";
        continue;
      }

      const originalHtml = source.innerHTML;
      let createdAccordionCount = 0;

      try {
        for (const heading of headings) {
          if (heading.previousElementSibling?.matches("hr")) {
            heading.previousElementSibling.remove();
          }
        }

        headings.forEach((heading, index) => {
          if (!heading.isConnected) return;

          const nextHeading = headings[index + 1];
          const range = document.createRange();
          range.setStartAfter(heading);

          if (nextHeading?.isConnected) {
            range.setEndBefore(nextHeading);
          } else if (source.lastChild) {
            range.setEndAfter(source.lastChild);
          } else {
            return;
          }

          const contentFragment = range.extractContents();
          this._trimDescriptionFragment(contentFragment);

          const hasContent =
            (contentFragment.textContent || "").trim() ||
            contentFragment.querySelector("img, video, iframe, table, ul, ol, blockquote");

          if (!hasContent) return;

          const details = document.createElement("details");
          details.className = "product-description-accordion";
          details.open = index === 0 && root.dataset.openFirst === "true";
          details.setAttribute("aria-expanded", String(details.open));

          const summary = document.createElement("summary");
          summary.className = "product-description-accordion__summary";

          const title = document.createElement("span");
          title.className = "product-description-accordion__title";
          title.textContent = heading.textContent.trim();

          const marker = document.createElement("span");
          marker.className = "product-description-accordion__marker";
          marker.setAttribute("aria-hidden", "true");

          const content = document.createElement("div");
          content.className = "product-description-accordion__content";
          content.append(contentFragment);

          summary.append(title, marker);
          details.append(summary, content);
          details.addEventListener("toggle", () => {
            details.setAttribute("aria-expanded", String(details.open));

            if (details.open && root.dataset.singleOpen === "true") {
              for (const sibling of root.querySelectorAll(".product-description-accordion[open]")) {
                if (sibling !== details) sibling.open = false;
              }
            }
          });

          heading.replaceWith(details);
          createdAccordionCount += 1;
        });
      } catch {
        source.innerHTML = originalHtml;
        root.dataset.accordionsEnhanced = "fallback";
        continue;
      }

      if (createdAccordionCount < minimumHeadings) {
        source.innerHTML = originalHtml;
        root.dataset.accordionsEnhanced = "fallback";
        continue;
      }

      source.classList.add("is-enhanced");
      root.dataset.accordionsEnhanced = "true";
    }
  }

  _trimDescriptionFragment(fragment) {
    const isDiscardableBoundary = (node) =>
      (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) ||
      (node.nodeType === Node.ELEMENT_NODE &&
        (node.matches("hr") ||
          (node.matches("p, div") && !node.textContent.trim() && !node.querySelector("img, video, iframe"))));

    while (fragment.firstChild && isDiscardableBoundary(fragment.firstChild)) {
      fragment.firstChild.remove();
    }

    while (fragment.lastChild && isDiscardableBoundary(fragment.lastChild)) {
      fragment.lastChild.remove();
    }
  }
}

if (!window.customElements.get("product-atelier")) {
  window.customElements.define("product-atelier", ProductAtelier);
}
(() => {
  const categoryLinks = document.querySelectorAll('[data-product-breadcrumb-category]');
  if (!categoryLinks.length || !document.referrer) return;

  try {
    const previousUrl = new URL(document.referrer);
    const collectionMatch = previousUrl.pathname.match(/^\/collections\/([^/]+)(?:\/|$)/);

    if (previousUrl.origin !== window.location.origin || !collectionMatch) return;

    const collectionHandle = decodeURIComponent(collectionMatch[1]);
    const collectionUrl = `/collections/${encodeURIComponent(collectionHandle)}`;

    fetch(`${collectionUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error('Collection lookup failed');
        return response.json();
      })
      .then((collection) => {
        if (!collection?.title) return;

        categoryLinks.forEach((categoryLink) => {
          categoryLink.href = `${previousUrl.pathname}${previousUrl.search}`;
          categoryLink.textContent = collection.title;
        });
      })
      .catch(() => {
        // Keep the deterministic server-rendered collection fallback.
      });
  } catch (_) {
    // Keep the server-rendered collection fallback.
  }
})();

(() => {
  const PAGE_ROOT = '.shopify-section--main-collection';

  class ProductBrowseRow {
    constructor(row) {
      this.row = row;
      this.track = row.querySelector('[data-product-browse-track]');
      this.list = row.querySelector('.product-browse-row__list');
      this.nextMarker = row.querySelector('[data-product-browse-next]');
      this.loader = row.querySelector('[data-product-browse-loader]');
      this.retry = row.querySelector('[data-product-browse-retry]');
      this.status = row.querySelector('[data-product-browse-status]');
      this.nextUrl = this.nextMarker?.dataset.productBrowseNext || '';
      this.loading = false;
      this.page = 1;

      if (!this.track || !this.list || !this.nextUrl) return;

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.retry?.addEventListener('click', () => this.loadNext());
    }

    onScroll() {
      const remaining = this.track.scrollWidth - this.track.clientWidth - this.track.scrollLeft;
      if (remaining < Math.max(900, this.track.clientWidth * 1.5)) this.loadNext();
    }

    handles() {
      return new Set(
        Array.from(this.list.querySelectorAll('product-card[handle]'))
          .map((card) => card.getAttribute('handle'))
          .filter(Boolean)
      );
    }

    scopeInteractiveIds(item) {
      item.querySelectorAll('[id]').forEach((element) => {
        if (!element.id.startsWith('quick-buy-')) return;
        const oldId = element.id;
        const newId = `${oldId}-row-${this.row.dataset.rowHandle}-${this.page}`;
        element.id = newId;
        item.querySelectorAll(`[aria-controls="${CSS.escape(oldId)}"]`).forEach((control) => {
          control.setAttribute('aria-controls', newId);
        });
      });
    }

    async loadNext() {
      if (this.loading || !this.nextUrl) return;
      this.loading = true;
      this.list.setAttribute('aria-busy', 'true');
      this.loader.hidden = false;
      this.loader.classList.add('is-loading');
      this.retry.hidden = true;
      this.status.textContent = 'Loading more products.';

      try {
        const response = await fetch(this.nextUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);

        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const collectionRoot = page.querySelector(PAGE_ROOT);
        const sourceList = collectionRoot?.querySelector('product-list.product-list');
        if (!sourceList) throw new Error('Recommendation products were missing.');

        const known = this.handles();
        const fragment = document.createDocumentFragment();
        let appended = 0;
        this.page += 1;

        Array.from(sourceList.children).forEach((item) => {
          const card = item.matches?.('product-card') ? item : item.querySelector?.('product-card');
          const handle = card?.getAttribute('handle');
          if (!card || (handle && known.has(handle))) return;
          if (handle) known.add(handle);
          this.scopeInteractiveIds(item);
          item.classList.add('ft-row-newly-loaded');
          fragment.appendChild(item);
          appended += 1;
        });

        this.list.appendChild(fragment);
        this.nextUrl =
          collectionRoot.querySelector('.collection-pagination-source a[rel="next"], .collection__pagination a[rel="next"]')?.href || '';

        requestAnimationFrame(() => {
          this.list.querySelectorAll('.ft-row-newly-loaded').forEach((item) => {
            item.classList.remove('ft-row-newly-loaded');
          });
        });

        this.status.textContent = appended ? 'More products loaded.' : '';
        if (!this.nextUrl) {
          this.track.removeEventListener('scroll', this.onScroll);
          this.loader.hidden = true;
        }
      } catch (_) {
        this.retry.hidden = false;
        this.status.textContent = 'More products could not be loaded. Use Try again.';
      } finally {
        this.loading = false;
        this.list.setAttribute('aria-busy', 'false');
        this.loader.classList.remove('is-loading');
      }
    }
  }

  const init = (scope = document) => {
    if (scope instanceof Element && scope.matches('[data-product-browse-row]')) {
      scope.dataset.productBrowseReady = 'true';
      new ProductBrowseRow(scope);
    }
    scope.querySelectorAll('[data-product-browse-row]').forEach((row) => {
      if (row.dataset.productBrowseReady === 'true') return;
      row.dataset.productBrowseReady = 'true';
      new ProductBrowseRow(row);
    });
  };

  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
